import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { CourseLayoutSelectorAtom } from "../../../../../../atoms/selectors/courseLayoutSelectorAtom/courseLayoutSelectorAtom";
import { BundledTransportationTypeSelect } from "./BundledTransportationTypeSelect";
import { TransportationTypeSelect } from "./TransportationTypeSelect";
import { TransportationTypeControlNode$key } from "./__generated__/TransportationTypeControlNode.graphql";

type Props = {
  nodeRef: TransportationTypeControlNode$key | null;
};

export const TransportationTypeControl = ({ nodeRef }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = useFragment(TransportationTypeControlFragment, nodeRef);

  const transportationTypes =
    data?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) || [];

  const isPrivate = data?.agenda?.facility?.club?.supportsPrivate;

  const layout = useAtomValue(CourseLayoutSelectorAtom);

  const singleTransportationType = transportationTypes.length === 1;
  const onlyTT = transportationTypes[0];
  const transportationIcon = GetTransportIconFromCode(
    (onlyTT.customCode || onlyTT?.code) ?? "",
    "mainTiny"
  );
  const rateForHoleCount =
    (layout?.holeCount === "EIGHTEEN"
      ? onlyTT.eighteenHolePrice
      : onlyTT.nineHolePrice) || 0;

  const shouldShowAsBundled =
    !isPrivate &&
    data?.agenda?.facility.publicBookingPermissionSet?.rateDisplay ===
      "BUNDLED";

  return (
    <VStack gap={4} padding={5} paddingBottom={3}>
      <Text variant="mainBaseSemiBold" pb={2}>{t`Transportation`}</Text>

      {singleTransportationType ? (
        <HStack gap={2} alignItems="center" px={4}>
          {transportationIcon ? transportationIcon({}) : null}
          <Text variant="mainSmallSemiBold">{onlyTT.name}</Text>
          <Text variant="mainSmallSemiBold" color="text-secondary">
            {rateForHoleCount
              ? `${currencySymbol}${ConvertCentsToDollars(rateForHoleCount)}`
              : ""}
          </Text>
        </HStack>
      ) : shouldShowAsBundled ? (
        <BundledTransportationTypeSelect timeSlotRef={data} />
      ) : (
        <TransportationTypeSelect fragmentRef={data ?? null} />
      )}
    </VStack>
  );
};

const TransportationTypeControlFragment = graphql`
  fragment TransportationTypeControlNode on Node
  @argumentDefinitions(
    memberId: { type: "ID", defaultValue: null }
    membershipTypeId: { type: "ID", defaultValue: null }
  ) {
    ... on Slot {
      ...BundledTransportationTypeSelectNode
        @arguments(memberId: $memberId, membershipTypeId: $membershipTypeId)
      ...TransportationTypeSelectNode @arguments(memberId: $memberId)
      agenda {
        facility {
          type
          publicBookingPermissionSet {
            rateDisplay
          }
          club {
            supportsPrivate
          }
        }
      }

      transportationTypes(memberId: $memberId, memberVisible: true) {
        id
        code
        customCode
        name
        enabled
        nineHolePrice: totalPrice(holeCount: NINE)
        eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
      }
    }
  }
`;
